import React, { useState, useMemo } from 'react';
import { FaTimes } from 'react-icons/fa';
import { saveAs } from 'file-saver';
import styles from './CheckDealTags.module.css';
import {
    formatBatchDateLong,
    getAsinThumbnailUrl,
    formatCouponText,
    mapItemToExcelRow,
    buildDealCheckWorkbook,
    groupBatchByAsin,
    getBatchDealTypeOptions,
} from './dealCheckerHistoryUtils';

// Deal Tag Validation detail view for a single batch. Self-contained (owns its
// filter state) so it can render inside CheckDealTags or the Manage page.
const DealTagValidation = ({ batch, onBack }) => {
    const [validationSearch, setValidationSearch] = useState('');
    const [productStatusFilter, setProductStatusFilter] = useState('all'); // 'all' | 'with_deals' | 'no_deals'
    const [validationDealType, setValidationDealType] = useState('');

    const validationGroupedByAsin = useMemo(
        () => groupBatchByAsin(batch, { search: validationSearch, productStatusFilter, dealType: validationDealType }),
        [batch, validationSearch, productStatusFilter, validationDealType]
    );
    const validationDealTypeOptions = useMemo(() => getBatchDealTypeOptions(batch), [batch]);
    const validationTotalActiveDeals = useMemo(
        () => (batch ? batch.results.filter(r => r.deal_present).length : 0),
        [batch]
    );

    if (!batch) return null;

    const asins = batch.results.map(r => r.asin).filter(Boolean);
    const asinRange = asins.length ? (asins.length === 1 ? asins[0] : `${asins[0]} - ${asins[asins.length - 1]}`) : '—';

    const renderPrimeDelivery = (primeDelivery) => {
        const text = primeDelivery || '-';
        if (text === '-') return text;
        const match = text.match(/^(.*?)(Order within[^.]*\.?)\s*$/i);
        if (match) {
            return <><span>{match[1].trim()}. </span><span className={styles.validationOrderWithin}>{match[2]}</span></>;
        }
        return text;
    };

    const handleDownloadValidationBatchExcel = () => {
        if (!batch || !batch.results.length) {
            alert('No batch data to export');
            return;
        }
        const successData = batch.results.filter(item => item.status === 'SUCCESS');
        if (successData.length === 0) {
            alert('No successful results in this batch to export');
            return;
        }
        const excelData = successData.map(mapItemToExcelRow);
        const excelBuffer = buildDealCheckWorkbook(excelData);
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const batchDate = batch.date ? new Date(batch.date).toISOString().slice(0, 10).replace(/-/g, '') : '';
        const filename = `deal_validation_batch_${batch.task_id || batchDate}_${timestamp}.xlsx`;
        saveAs(blob, filename);
    };

    return (
        <div className={styles.validationPage}>
            <div className={styles.validationHeader}>
                <div className={styles.validationHeaderTop}>
                    <button type="button" onClick={onBack} className={styles.validationBackBtn} aria-label="Back">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M25 13.75H9.7875L16.775 6.7625L15 5L5 15L15 25L16.7625 23.2375L9.7875 16.25H25V13.75Z" fill="#505050"/>
                    </svg>
                    </button>
                    <h1 className={styles.validationTitle}>Deal Tag Validation</h1>
                </div>
                <div className={styles.validationAsinBadge}>{asinRange}</div>
                <div className={styles.validationDate}>{formatBatchDateLong(batch.date)}</div>
            </div>
            <div className={styles.validationSearchRow}>
                <div className={styles.historySearchBarBox}>
                    <div className={styles.historySearchBarInner}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.421 0C14.5954 0 18 3.40462 18 7.57899C18 11.7534 14.5954 15.158 10.421 15.158C8.67422 15.158 7.06155 14.5613 5.77761 13.5621L1.61719 17.7224C1.24712 18.0925 0.647591 18.0925 0.277544 17.7224C-0.0925028 17.3524 -0.0925265 16.7519 0.277544 16.3828L4.43796 12.2234C3.43876 10.9383 2.84203 9.32678 2.84203 7.58006C2.84203 3.40569 6.24668 0.00106589 10.4211 0.00106589L10.421 0ZM10.421 1.89475C7.27068 1.89475 4.73667 4.42874 4.73667 7.57899C4.73667 10.7292 7.27068 13.2632 10.421 13.2632C13.5712 13.2632 16.1052 10.7292 16.1052 7.57899C16.1052 4.42874 13.5712 1.89475 10.421 1.89475Z" fill="#D2D2D2"/>
                        </svg>
                        <input
                            id="validationSearch"
                            type="text"
                            className={`${styles.historySearchBarInput} ${validationSearch ? styles.historySearchBarInputFilled : ''}`}
                            value={validationSearch}
                            onChange={(e) => setValidationSearch(e.target.value)}
                            placeholder="Search for ASIN and SKU-IDs"
                        />
                        {validationSearch && (
                            <button type="button" onClick={() => setValidationSearch('')} className={styles.historyClearSearchBtn} aria-label="Clear search">
                                <FaTimes className={styles.historyClearSearchBtnIcon} />
                            </button>
                        )}
                    </div>
                </div>
                <div className={styles.validationTotalDealsWrap}>
                    <div className={styles.validationTotalDeals}>
                        <span className={styles.validationTotalDealsLabel}>Total Active Deals: </span>
                        <span className={styles.validationTotalDealsValue}>{validationTotalActiveDeals}</span>
                    </div>
                    <button
                        type="button"
                        className={styles.validationDownloadBtn}
                        onClick={handleDownloadValidationBatchExcel}
                    >
                        <span>Download</span>
                    </button>
                </div>
            </div>
            <div className={styles.validationFilters}>
                <div className={styles.validationFiltersLeft}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.6431 0H2.35692C1.05846 0 0 1.05846 0 2.35692C0 2.98462 0.246154 3.57538 0.689231 4.02462L5.53846 8.87385V14.5169C5.53846 15.3354 6.20308 16 7.02154 16C7.41538 16 7.79692 15.84 8.07385 15.5631L9.92 13.7169C10.2708 13.3662 10.4615 12.9046 10.4615 12.4123V8.86769L15.3108 4.01846C15.7538 3.57538 16 2.98462 16 2.35692C16 1.05846 14.9415 0 13.6431 0ZM14.4369 3.15077L9.59385 8C9.36 8.23385 9.23077 8.54154 9.23077 8.87385V12.4185C9.23077 12.5785 9.16308 12.7385 9.05231 12.8554L7.20615 14.7015C7.05846 14.8431 6.76923 14.72 6.76923 14.5169V8.87385C6.76923 8.54769 6.64 8.23385 6.40615 8.00615L1.56308 3.15077C1.35385 2.94154 1.23077 2.65231 1.23077 2.35692C1.23077 1.73538 1.73538 1.23077 2.35692 1.23077H13.6431C14.2646 1.23077 14.7692 1.73538 14.7692 2.35692C14.7692 2.65231 14.6462 2.94154 14.4369 3.15077Z" fill="#737373"/>
                    </svg>
                    <span className={styles.validationFiltersLabel}>Filters:</span>
                    <div className={styles.validationTabs}>
                        <button type="button" className={productStatusFilter === 'all' ? styles.validationTabActive : styles.validationTab} onClick={() => setProductStatusFilter('all')}>All Products</button>
                        <button type="button" className={productStatusFilter === 'with_deals' ? styles.validationTabActive : styles.validationTab} onClick={() => setProductStatusFilter('with_deals')}>With Deals</button>
                        <button type="button" className={productStatusFilter === 'no_deals' ? styles.validationTabActive : styles.validationTab} onClick={() => setProductStatusFilter('no_deals')}>No Deals</button>
                    </div>
                    <span className={styles.validationFiltersDivider}>|</span>
                    <div className={styles.validationSelectWrap}>
                        <select
                            className={styles.validationDropdownSelect}
                            value={validationDealType}
                            onChange={(e) => setValidationDealType(e.target.value)}
                        >
                            <option value="">Deal type</option>
                            {validationDealTypeOptions.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                        <span className={styles.validationDropdownArrow} aria-hidden>
                            <svg width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1.88 0L8 5.9336L14.12 0L16 1.82672L8 9.6L0 1.82672L1.88 0Z" fill="#A4A4A4"/>
                            </svg>
                        </span>
                    </div>
                </div>
                <button type="button" className={styles.validationClearFilters} onClick={() => { setValidationSearch(''); setProductStatusFilter('all'); setValidationDealType(''); }}>Clear all filters</button>
            </div>
            <div className={styles.validationTableCard}>
                <div className={styles.validationResultsSummary}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g opacity="0.7">
                    <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="#6794C5"/>
                    <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="#6794C5"/>
                    </g>
                    </svg>
                    <span className={styles.validationResultsSummaryText}> Showing Results for {validationGroupedByAsin.length} ASINs</span>
                </div>
                <div className={styles.validationTableHead}>
                    <div className={styles.validationThProduct}>Product & ASIN</div>
                    <div className={styles.validationThPincode}>Pincode</div>
                    <div className={styles.validationThDeal}>Deal Badge</div>
                    <div className={styles.validationThCoupon}>Coupon</div>
                    <div className={styles.validationThDealPrice}>Deal Selling Price</div>
                    <div className={styles.validationThBuybox}>Buy Box Winner</div>
                    <div className={styles.validationThDelivery}>Delivery</div>
                    <div className={styles.validationThPrime}>Prime Delivery</div>
                </div>
                <div className={styles.validationTableBody}>
                    {validationGroupedByAsin.length === 0 ? (
                        <div className={styles.validationNoResults}>No ASINs match your filters. Try clearing search or changing the product filter.</div>
                    ) : validationGroupedByAsin.map((group) => (
                        <div key={group.asin} className={styles.validationAsinCard}>
                            <div className={styles.validationProductCol}>
                                <div className={styles.validationProductThumb}>
                                    <img
                                        src={group.imageUrl}
                                        alt={group.title || group.asin || 'Product image'}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = getAsinThumbnailUrl(group.asin);
                                        }}
                                    />
                                </div>
                                <div className={styles.validationProductInfo}>
                                    <div className={styles.validationProductTitle}>{group.title}</div>
                                    <div className={styles.validationAsinBadgeSmall}>{group.asin}</div>
                                </div>
                            </div>
                            <div className={styles.validationRowsCol}>
                                {group.rows.map((row, idx) => (
                                    <div key={`${group.asin}-${row.zipcode}-${idx}`} className={styles.validationRow}>
                                        <div className={styles.validationCellPincode}>{row.zipcode}</div>
                                        <div className={styles.validationCellDeal}>
                                            {row.deal_present && row.deal_badge && row.deal_badge !== 'Not applicable' && row.deal_badge !== 'N/A' ? (
                                                <span className={styles.validationDealBadge}>{row.deal_badge}</span>
                                            ) : (
                                                <span className={styles.validationDealNA}>Not Applicable</span>
                                            )}
                                        </div>
                                        <div className={styles.validationCellCoupon}>
                                            {(row.has_coupon && row.coupon_text && row.coupon_text !== 'N/A') ? (
                                                <span className={styles.validationDealBadge}>{formatCouponText(row.coupon_text)}</span>
                                            ) : '-'}
                                        </div>
                                        <div className={styles.validationCellDealPrice}>{row.current_price && row.current_price !== 'N/A' ? row.current_price : '-'}</div>
                                        <div className={styles.validationCellBuybox}>{row.sold_by && row.sold_by !== 'N/A' ? row.sold_by : '-'}</div>
                                        <div className={styles.validationCellDelivery}>{row.standardDelivery || '-'}</div>
                                        <div className={styles.validationCellPrime}>{renderPrimeDelivery(row.primeDelivery)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default DealTagValidation;
